import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";

function fmtLocal(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Schedule a future publish for a content key. `getPayload` must return the
// current content dict to publish at the chosen time.
export default function ScheduleControl({ contentKey, getPayload, disabled, testIdPrefix = "cms-schedule" }) {
  const [pending, setPending] = useState(null);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/content/${contentKey}/schedule`);
      setPending(r.data || null);
    } catch {
      setPending(null);
    }
  }, [contentKey]);
  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    if (!when) { toast.error("Pick a date & time first"); return; }
    const dt = new Date(when);
    if (isNaN(dt.getTime())) { toast.error("Invalid date & time"); return; }
    if (dt.getTime() <= Date.now()) { toast.error("Choose a time in the future"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/content/${contentKey}/schedule`, {
        publish_at: dt.toISOString(),
        payload: getPayload(),
      });
      toast.success(`Scheduled to publish on ${dt.toLocaleString()}.`);
      setWhen("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/content/${contentKey}/schedule`);
      toast.info("Schedule cancelled.");
      setPending(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  if (disabled) return null;

  return (
    <div className="rounded-md border border-[var(--border-lch)] bg-[var(--panel)] p-3" data-testid={`${testIdPrefix}-${contentKey}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--muted)] mb-2">
        <CalendarClock size={14} className="text-[#c9a86a]" /> Schedule publish
      </div>
      {pending ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-[var(--text)]">
            <Badge variant="outline" className="mr-2 text-[10px] bg-[#c9a86a]/15 text-[#9a7b3f] border-[#c9a86a]/40">Pending</Badge>
            Goes live on <span className="font-medium">{fmtLocal(pending.publish_at)}</span>
          </div>
          <Button size="sm" variant="outline" onClick={cancel} disabled={busy} data-testid={`${testIdPrefix}-cancel`} className="text-red-500">
            <X size={13} className="mr-1" />Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-[var(--panel-2)] w-auto" data-testid={`${testIdPrefix}-input`} />
          <Button size="sm" onClick={schedule} disabled={busy} data-testid={`${testIdPrefix}-set`} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]">
            {busy ? "Scheduling\u2026" : "Schedule"}
          </Button>
        </div>
      )}
      <p className="text-[11px] text-[var(--muted)] mt-2">Saves the current content and publishes it automatically at the chosen time.</p>
    </div>
  );
}
